import { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  HOME_GLASS_BACKDROP,
  HOME_GLASS_BORDER,
} from "../../../pages/Home/constants/homeGlass";
import {
  MODAL_HEIGHT_SM,
  MODAL_MAX_HEIGHT_XS,
  SAFE_AREA_BOTTOM,
  SAFE_AREA_TOP,
} from "../../../layout/layoutMetrics";
import { messageService } from "../../../core/services/message.service";

const THEME_ACCENT = "#D4A017";
const SIDEBAR_GAP = "14px";
const MODAL_LEFT_INSET = `calc(35px + 230px + ${SIDEBAR_GAP})`;
const MODAL_RIGHT_MARGIN = "8px";
const MODAL_VERTICAL_INSET_SM = "20px";
const MODAL_WIDTH = `min(960px, calc(100vw - 35px - 230px - ${SIDEBAR_GAP} - ${MODAL_RIGHT_MARGIN}))`;
const MODAL_HEIGHT = MODAL_HEIGHT_SM;

const briefMarkdownSx = {
  fontSize: 14.2,
  lineHeight: 1.75,
  fontFamily: "'Inter', sans-serif",
  fontWeight: 400,
  color: "rgba(234,231,226,0.9)",
  "& p": {
    marginTop: 0,
    marginBottom: "0.85em",
    "&:last-child": { marginBottom: 0 },
  },
  "& ul, & ol": {
    marginTop: 0,
    marginBottom: "0.85em",
    paddingLeft: "1.35em",
  },
  "& li": {
    marginBottom: "0.35em",
  },
  "& strong": {
    fontWeight: 650,
    color: "rgba(255,255,255,0.95)",
  },
  "& code": {
    fontFamily:
      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
    fontSize: "0.9em",
    px: 0.5,
    py: 0.15,
    borderRadius: "4px",
    background: "rgba(255,255,255,0.08)",
  },
};

function unwrapLockedMessage(payload) {
  if (!payload) return null;
  if (payload?.data && typeof payload.data === "object") {
    if (payload.data.assistantMessage || payload.data.userMessage) {
      return payload.data;
    }
  }
  if (payload.assistantMessage || payload.userMessage) return payload;
  return null;
}

function BriefContent({ text }) {
  if (!text) return null;

  return (
    <Box
      sx={{
        px: { xs: 1.75, sm: 2.25 },
        py: { xs: 1.75, sm: 2.25 },
        borderRadius: "14px",
        background:
          "linear-gradient(145deg, rgba(255,255,255,0.07), rgba(255,255,255,0.03))",
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
      }}
    >
      <Box sx={briefMarkdownSx}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
      </Box>
    </Box>
  );
}

function StatusMessage({ children, tone = "muted" }) {
  const color =
    tone === "error" ? "rgba(243,201,74,0.92)" : "rgba(255,255,255,0.5)";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 180,
        px: 2,
        textAlign: "center",
      }}
    >
      <Typography sx={{ fontSize: 14, lineHeight: 1.6, color }}>{children}</Typography>
    </Box>
  );
}

/**
 * Side panel for the locked final brief message.
 *
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {string|number|null} clientId
 * @param {string|number|null} projectId
 */
export default function FinalBriefModal({ open, onClose, clientId, projectId }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lockedMessage, setLockedMessage] = useState(null);

  useEffect(() => {
    if (!open) {
      setLoading(false);
      setError(null);
      setLockedMessage(null);
      return undefined;
    }

    if (clientId == null || projectId == null) {
      setLockedMessage(null);
      setError("Client or project is missing.");
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const loadLockedMessage = async () => {
      setLoading(true);
      setError(null);
      setLockedMessage(null);

      try {
        const response = await messageService.getLockedMessage(
          clientId,
          projectId,
        );
        if (cancelled) return;
        setLockedMessage(unwrapLockedMessage(response));
      } catch (err) {
        if (cancelled) return;
        setLockedMessage(null);
        if (err?.response?.status === 404) {
          setError("No final brief has been locked for this project yet. Lock a conversation to create a final brief.");
        } else {
        setError(
          err?.message ||
            err?.response?.data?.message ||
            "Failed to load final brief.",
        );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadLockedMessage();

    return () => {
      cancelled = true;
    };
  }, [open, clientId, projectId]);

  const assistantText =
    lockedMessage?.assistantMessage?.contentText?.trim() || "";
  const hasContent = Boolean(assistantText);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      hideBackdrop
      disableScrollLock
      aria-labelledby="final-brief-title"
      sx={{
        pointerEvents: "none",
        "& .MuiDialog-container": {
          pointerEvents: "none",
          justifyContent: "flex-start",
          alignItems: { xs: "center", sm: "stretch" },
          paddingLeft: { xs: 1, sm: MODAL_LEFT_INSET },
          paddingRight: { xs: 1, sm: MODAL_RIGHT_MARGIN },
          paddingTop: {
            xs: `max(8px, ${SAFE_AREA_TOP})`,
            sm: MODAL_VERTICAL_INSET_SM,
          },
          paddingBottom: {
            xs: `max(8px, ${SAFE_AREA_BOTTOM})`,
            sm: MODAL_VERTICAL_INSET_SM,
          },
          boxSizing: "border-box",
        },
      }}
      PaperProps={{
        sx: {
          pointerEvents: "auto",
          m: 0,
          width: {
            xs: "calc(100vw - 16px)",
            sm: MODAL_WIDTH,
          },
          height: {
            xs: "auto",
            sm: MODAL_HEIGHT,
          },
          maxWidth: {
            xs: "min(100%, calc(100vw - 16px))",
            sm: MODAL_WIDTH,
          },
          maxHeight: {
            xs: MODAL_MAX_HEIGHT_XS,
            sm: MODAL_HEIGHT,
          },
          borderRadius: "16px",
          overflow: "hidden",
          background:
            "linear-gradient(145deg, rgba(64, 64, 53, 0.78), rgba(28, 29, 22, 0.85)), radial-gradient(circle at 0% 0%, rgba(219, 165, 41, 0.12), transparent 50%)",
          backdropFilter: HOME_GLASS_BACKDROP,
          WebkitBackdropFilter: HOME_GLASS_BACKDROP,
          border: HOME_GLASS_BORDER,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          color: "#fff",
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          pt: 2.5,
          pb: 2.25,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          height: "100%",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexShrink: 0,
            mb: 2.5,
            pb: 2,
            borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                display: "grid",
                placeItems: "center",
                background: `${THEME_ACCENT}22`,
                border: `1px solid ${THEME_ACCENT}44`,
              }}
            >
              <DescriptionOutlinedIcon sx={{ color: THEME_ACCENT, fontSize: 22 }} />
            </Box>
            <Box>
              <Typography
                id="final-brief-title"
                component="h2"
                sx={{
                  fontSize: 18,
                  fontWeight: 700,
                  letterSpacing: "-0.01em",
                  color: "rgba(255,255,255,0.95)",
                }}
              >
                Final Brief
              </Typography>
              <Typography
                sx={{ fontSize: 12, color: "rgba(255,255,255,0.45)", mt: 0.2 }}
              >
                Locked conversation brief
              </Typography>
            </Box>
          </Stack>

          <IconButton
            aria-label="Close final brief"
            onClick={onClose}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.55)",
              "&:hover": {
                color: "rgba(255,255,255,0.9)",
                bgcolor: "rgba(255,255,255,0.08)",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: "1 1 auto",
            minHeight: 0,
            overflowY: "auto",
            pr: 0.5,
            "&::-webkit-scrollbar": { width: 5 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(255,255,255,0.22)",
              borderRadius: 10,
            },
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 180,
              }}
            >
              <CircularProgress size={22} sx={{ color: "rgba(255,255,255,0.5)" }} />
            </Box>
          ) : error ? (
            <StatusMessage tone="error">{error}</StatusMessage>
          ) : !hasContent ? (
            <StatusMessage>
              No locked brief is available for this project yet.
            </StatusMessage>
          ) : (
            <Box sx={{ pb: 1 }}>
              <BriefContent text={assistantText} />
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}
